import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Trash2, CheckCircle, Circle, Calendar, Star, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITIES = [
  { value: 'high', label: 'Hög', color: 'bg-red-100 text-red-600 border-red-200' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-600 border-amber-200' },
  { value: 'low', label: 'Låg', color: 'bg-green-100 text-green-600 border-green-200' },
];

export default function TodoPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTodo, setNewTodo] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [filter, setFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      const res = await axios.get(`${API}/api/todos`, { headers: getAuthHeader() });
      setTodos(res.data);
    } catch (error) {
      // If endpoint doesn't exist yet, use localStorage as fallback
      const saved = localStorage.getItem('livon-todos');
      if (saved) setTodos(JSON.parse(saved));
    } finally {
      setLoading(false);
    }
  };

  const saveTodos = (newTodos) => {
    setTodos(newTodos);
    localStorage.setItem('livon-todos', JSON.stringify(newTodos));
    // Also try to save to backend
    axios.put(`${API}/api/todos/bulk`, { todos: newTodos }, { headers: getAuthHeader() }).catch(() => {});
  };

  const addTodo = () => {
    if (!newTodo.trim()) return;
    const todo = {
      id: Date.now().toString(),
      text: newTodo,
      completed: false,
      priority: newPriority,
      createdAt: new Date().toISOString(),
    };
    const newTodos = [todo, ...todos];
    saveTodos(newTodos);
    setNewTodo('');
    toast.success('Uppgift tillagd');
  };

  const toggleTodo = (id) => {
    const newTodos = todos.map(t => 
      t.id === id ? { ...t, completed: !t.completed, completedAt: !t.completed ? new Date().toISOString() : null } : t
    );
    saveTodos(newTodos);
  };

  const deleteTodo = (id) => {
    const newTodos = todos.filter(t => t.id !== id);
    saveTodos(newTodos);
    toast.success('Uppgift borttagen');
  };

  const clearCompleted = () => {
    const newTodos = todos.filter(t => !t.completed);
    saveTodos(newTodos);
    toast.success('Klara uppgifter rensade');
  };

  // Filter todos
  const filteredTodos = todos.filter(t => {
    if (!showCompleted && t.completed) return false;
    if (filter === 'all') return true;
    if (filter === 'active') return !t.completed;
    if (filter === 'completed') return t.completed;
    return t.priority === filter;
  });

  // Sort: incomplete first, then by priority
  const sortedTodos = [...filteredTodos].sort((a, b) => {
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const completedCount = todos.filter(t => t.completed).length;
  const totalCount = todos.length;

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="todo-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Att göra</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att använda att-göra-listan.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">
            Uppgradera nu
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="todo-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Att göra</h1>
            <p className="text-xs text-muted-foreground">{completedCount} av {totalCount} klart</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Progress */}
        {totalCount > 0 && (
          <div className="bg-white rounded-2xl p-4 border border-border/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Framsteg</span>
              <span className="text-sm text-muted-foreground">{Math.round((completedCount / totalCount) * 100)}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-sky-400 to-sky-500 transition-all duration-500"
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Add todo */}
        <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
          <p className="text-sm font-medium text-foreground">Skriv en ny uppgift:</p>
          <div className="flex gap-2">
            <Input
              placeholder="T.ex. Handla mjölk, ringa tandläkaren ..."
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTodo()}
              className="rounded-xl flex-1"
              data-testid="todo-input"
            />
            <Button onClick={addTodo} className="rounded-xl px-4" data-testid="add-todo-btn">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till
            </Button>
          </div>
          
          {/* Priority selector */}
          <div className="flex gap-2">
            <span className="text-sm text-muted-foreground self-center">Prioritet:</span>
            {PRIORITIES.map(p => (
              <Button
                key={p.value}
                variant={newPriority === p.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setNewPriority(p.value)}
                className="rounded-full text-xs"
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { value: 'all', label: 'Alla' },
            { value: 'active', label: 'Aktiva' },
            { value: 'completed', label: 'Klara' },
            { value: 'high', label: '🔴 Hög' },
            { value: 'medium', label: '🟡 Medium' },
            { value: 'low', label: '🟢 Låg' },
          ].map(f => (
            <Button
              key={f.value}
              variant={filter === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.value)}
              className="rounded-full whitespace-nowrap"
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Todo list */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-16 bg-secondary/50 rounded-xl animate-pulse" />)}
          </div>
        ) : sortedTodos.length > 0 ? (
          <div className="space-y-2">
            {sortedTodos.map(todo => {
              const priority = PRIORITIES.find(p => p.value === todo.priority);
              return (
                <div 
                  key={todo.id}
                  className={`bg-white rounded-xl p-4 border border-border/30 flex items-center gap-3 transition-all ${todo.completed ? 'opacity-60' : ''}`}
                  data-testid={`todo-item-${todo.id}`}
                >
                  <button
                    onClick={() => toggleTodo(todo.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      todo.completed 
                        ? 'bg-sky-500 border-sky-500 text-white' 
                        : 'border-border hover:border-sky-400'
                    }`}
                    data-testid={`toggle-todo-${todo.id}`}
                  >
                    {todo.completed && <CheckCircle className="w-4 h-4" />}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm ${todo.completed ? 'line-through text-muted-foreground' : ''}`}>
                      {todo.text}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${priority?.color}`}>
                        {priority?.label}
                      </span>
                      {todo.completedAt && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {new Date(todo.completedAt).toLocaleDateString('sv-SE')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => deleteTodo(todo.id)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`delete-todo-${todo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {filter === 'completed' ? 'Inga klara uppgifter' : 'Lägg till din första uppgift'}
            </p>
          </div>
        )}

        {/* Clear completed */}
        {completedCount > 0 && (
          <div className="text-center">
            <Button 
              variant="outline" 
              onClick={clearCompleted}
              className="rounded-full text-sm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Rensa klara uppgifter ({completedCount})
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
